import {
  ChangeDetectionStrategy,
  Component,
  Signal,
  computed,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BdfJob } from '@infinitform/platform-core';
import { ModalComponent } from '../modal/modal.component';
import { ButtonComponent } from '../forms/button/button.component';
import { CheckboxComponent } from '../forms/checkbox/checkbox.component';
import { InlineSpinnerComponent } from '../spinner/inline-spinner.component';
import { BdfRowPillComponent } from '../bdf-row-pill/bdf-row-pill.component';
import { BdfHighresBadgeComponent } from '../bdf-highres-badge/bdf-highres-badge.component';

export interface BdfProjectSummary {
  id: string;
  name: string;
  isMaster: boolean;
}

export interface BdfChildSummary {
  id: string;
  name: string;
  methodLabel: string;
}

export interface BdfModalData {
  project: BdfProjectSummary;
  children?: BdfChildSummary[];
  statusFor: (projectId: string) => BdfJob | null;
  closeFn?: () => void;
}

type Listener<E> = (event: E) => void;
type Subscription = { unsubscribe: () => void };

function eventChannel<E>(): {
  emit: (event: E) => void;
  subscribe: (fn: Listener<E>) => Subscription;
} {
  const listeners: Listener<E>[] = [];
  return {
    emit: (event) => listeners.slice().forEach((fn) => fn(event)),
    subscribe: (fn) => {
      listeners.push(fn);
      return { unsubscribe: () => listeners.splice(listeners.indexOf(fn), 1) };
    },
  };
}

// Cabinet-side BDF generation modal. Does NOT inject MODAL_DATA / MODAL_REF
// because cabinet and editor each ship their own token instances, so DI
// would only see one of them. Callers seed it via setData() after open.
@Component({
  selector: 'app-bdf-generation-modal',
  templateUrl: './bdf-generation-modal.component.html',
  styleUrls: ['./bdf-generation-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ModalComponent, ButtonComponent, CheckboxComponent, BdfRowPillComponent, BdfHighresBadgeComponent, InlineSpinnerComponent, FormsModule],
})
export class BdfGenerationModalComponent {
  private readonly _project = signal<BdfProjectSummary | null>(null);
  private readonly _children = signal<BdfChildSummary[]>([]);
  private readonly _statusFor = signal<(id: string) => BdfJob | null>(() => null);
  private closeFn: (() => void) | null = null;

  readonly project = this._project.asReadonly();
  readonly children = this._children.asReadonly();
  readonly statusFor = this._statusFor.asReadonly();
  readonly selectedChildIds = signal<Set<string>>(new Set());
  /** "High resolution" mesh toggle — finer fTetWild target edge length (0.02 vs 0.03). */
  readonly highRes = signal(false);

  private readonly generateBus = eventChannel<{ childIds: string[] | null; highRes: boolean }>();
  private readonly cancelBus = eventChannel<{ projectId: string }>();
  private readonly downloadBus = eventChannel<{ projectId: string }>();
  private readonly downloadBundleBus = eventChannel<{ childIds: string[] }>();
  private readonly closeBus = eventChannel<void>();

  readonly generate = { subscribe: this.generateBus.subscribe };
  readonly cancel = { subscribe: this.cancelBus.subscribe };
  readonly download = { subscribe: this.downloadBus.subscribe };
  readonly downloadBundle = { subscribe: this.downloadBundleBus.subscribe };
  readonly close = { subscribe: this.closeBus.subscribe };

  // Per-action click guard. Auto-released after 3s as a safety net in case
  // the WS status update never arrives, so buttons don't stay locked forever.
  private readonly busyKeys = signal<Set<string>>(new Set());
  private isBusy(key: string): boolean { return this.busyKeys().has(key); }
  private lockBusy(key: string): void {
    this.busyKeys.update((s) => { const n = new Set(s); n.add(key); return n; });
    setTimeout(() => this.unlockBusy(key), 3000);
  }
  private unlockBusy(key: string): void {
    this.busyKeys.update((s) => { if (!s.has(key)) return s; const n = new Set(s); n.delete(key); return n; });
  }
  isActionBusy(key: string): boolean { return this.busyKeys().has(key); }

  // Bundle download has its own state — the HTTP fetch can run well past the
  // 3s click-guard window. Consumer flips it via setBundleBusy() around the
  // actual fetch so the button shows a spinner for the whole duration.
  private readonly _bundleBusy = signal(false);
  readonly bundleBusy = this._bundleBusy.asReadonly();
  setBundleBusy(busy: boolean): void { this._bundleBusy.set(busy); }

  setData(data: BdfModalData): void {
    this._project.set(data.project);
    this._children.set(data.children ?? []);
    this._statusFor.set(data.statusFor);
    this.closeFn = data.closeFn ?? null;
  }

  readonly singleStatus = computed<BdfJob | null>(() => {
    const proj = this._project();
    if (!proj || proj.isMaster) return null;
    return this._statusFor()(proj.id);
  });

  readonly bundleReady = computed<boolean>(() => {
    const sel = this.selectedChildIds();
    if (sel.size === 0) return false;
    const fn = this._statusFor();
    return [...sel].every((id) => fn(id)?.status === 'done');
  });

  readonly generateDisabled = computed<boolean>(() => {
    const proj = this._project();
    if (!proj) return true;
    if (this.busyKeys().has(`generate:${proj.id}`)) return true;
    if (proj.isMaster) {
      const sel = this.selectedChildIds();
      if (sel.size === 0) return true;
      const fn = this._statusFor();
      // Enabled if at least one selected child isn't already in-flight —
      // done children are regenerated, idle/error are generated.
      return ![...sel].some((id) => {
        const st = fn(id)?.status;
        return st !== 'queued' && st !== 'running';
      });
    }
    const st = this.singleStatus()?.status;
    return st === 'queued' || st === 'running';
  });

  getChildStatus(childId: string): Signal<BdfJob | null> {
    return computed(() => this._statusFor()(childId));
  }

  isChildSelected(childId: string): boolean {
    return this.selectedChildIds().has(childId);
  }

  toggleChild(childId: string, checked: boolean): void {
    this.selectedChildIds.update((set) => {
      const next = new Set(set);
      if (checked) next.add(childId); else next.delete(childId);
      return next;
    });
  }

  onGenerate(): void {
    const proj = this._project();
    if (!proj) return;
    const key = `generate:${proj.id}`;
    if (this.isBusy(key)) return;
    const highRes = this.highRes();
    if (proj.isMaster) {
      // Skip only the in-flight ones; done children re-run as a regenerate
      // with the modal's current high-res state.
      const fn = this._statusFor();
      const childIds = [...this.selectedChildIds()].filter((id) => {
        const st = fn(id)?.status;
        return st !== 'queued' && st !== 'running';
      });
      if (childIds.length === 0) return;
      this.lockBusy(key);
      this.generateBus.emit({ childIds, highRes });
    } else {
      this.lockBusy(key);
      this.generateBus.emit({ childIds: null, highRes });
    }
  }

  toggleHighRes(checked: boolean): void {
    this.highRes.set(checked);
  }

  onCancelSingle(): void {
    const proj = this._project();
    if (!proj) return;
    const key = `cancel:${proj.id}`;
    if (this.isBusy(key)) return;
    this.lockBusy(key);
    this.cancelBus.emit({ projectId: proj.id });
  }

  onCancelChild(childId: string): void {
    const key = `cancel:${childId}`;
    if (this.isBusy(key)) return;
    this.lockBusy(key);
    this.cancelBus.emit({ projectId: childId });
  }

  /** Re-run for a single-MF project — emits with the modal's current high-res state. */
  onRegenerateSingle(): void {
    const proj = this._project();
    if (!proj) return;
    const key = `generate:${proj.id}`;
    if (this.isBusy(key)) return;
    this.lockBusy(key);
    this.generateBus.emit({ childIds: null, highRes: this.highRes() });
  }

  /** Re-run for a single child row — emits with this child's id and current high-res state. */
  onRegenerateChild(childId: string): void {
    const key = `generate:${this._project()?.id ?? childId}`;
    if (this.isBusy(key)) return;
    this.lockBusy(key);
    this.generateBus.emit({ childIds: [childId], highRes: this.highRes() });
  }

  onDownloadSingle(): void {
    const proj = this._project();
    if (!proj) return;
    const key = `download:${proj.id}`;
    if (this.isBusy(key)) return;
    this.lockBusy(key);
    this.downloadBus.emit({ projectId: proj.id });
  }

  onDownloadChild(childId: string): void {
    const key = `download:${childId}`;
    if (this.isBusy(key)) return;
    this.lockBusy(key);
    this.downloadBus.emit({ projectId: childId });
  }

  onDownloadBundle(): void {
    const proj = this._project();
    if (!proj) return;
    const key = `bundle:${proj.id}`;
    if (this.isBusy(key)) return;
    this.lockBusy(key);
    this.downloadBundleBus.emit({ childIds: [...this.selectedChildIds()] });
  }

  onClose(): void {
    this.closeBus.emit();
    this.closeFn?.();
  }
}
